import React from "react";
import {
  View,
  TouchableOpacity,
  Modal,
  ScrollView,
  Platform,
} from "react-native";
import Constants from "expo-constants";
import Text from "app/components/Text";
import store from "app/redux/store";
import offlineQueueUtil from "app/utils/offlineQueue";
import ENV from "app/utils/ENV";
import { update as updateValue } from "app/actions/values";

export default class OfflineBanner extends React.Component {
  state = {
    isOnline: true,
    offlineQueue: [],
    pendingConflicts: [],
    showDetail: false,
    selectedConflict: null,
  };

  componentDidMount() {
    const { app } = store.getState();
    this.setState({
      isOnline: app.isOnline,
      offlineQueue: app.offlineQueue || [],
      pendingConflicts: app.pendingConflicts || [],
    });
    this.unsubscribe = store.subscribe(() => {
      const { app } = store.getState();
      this.setState({
        isOnline: app.isOnline,
        offlineQueue: app.offlineQueue || [],
        pendingConflicts: app.pendingConflicts || [],
      });
    });
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  resolveConflict = (conflict, keepMine) => {
    if (!keepMine && conflict.serverValue) {
      store.dispatch(updateValue(conflict.serverValue));
    }
    store.dispatch({ type: "app/resolve_conflict", id: conflict.id });
    this.setState({ selectedConflict: null });
  };

  renderConflictDetail() {
    const { selectedConflict } = this.state;
    if (!selectedConflict) return null;

    const local = selectedConflict.localData?.value || {};
    const server = selectedConflict.serverValue || {};

    const fields = [
      { key: "title", label: "Title" },
      { key: "text", label: "Body" },
    ].filter(({ key }) => local[key] !== undefined || server[key] !== undefined);

    return (
      <Modal
        visible={!!selectedConflict}
        transparent
        animationType="fade"
        onRequestClose={() => this.setState({ selectedConflict: null })}
      >
        {/* Full-screen flex column so nothing gets clipped */}
        <View style={{ flex: 1, backgroundColor: "#181818", paddingTop: Constants.statusBarHeight }}>
          {/* Fixed header */}
          <View style={{
            paddingHorizontal: 16,
            paddingVertical: 14,
            borderBottomWidth: 1,
            borderBottomColor: "#2a2a2a",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
            <View style={{ flex: 1 }}>
              <Text style={{ color: "#e67e22", fontSize: 11, fontWeight: "600", letterSpacing: 0.5, marginBottom: 2 }}>
                SYNC CONFLICT
              </Text>
              <Text style={{ color: "#ddd", fontSize: 14, fontWeight: "600" }}>
                {selectedConflict.description}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => this.setState({ selectedConflict: null })}
              style={{ padding: 8, marginLeft: 8 }}
            >
              <Text style={{ color: "#555", fontSize: 20, lineHeight: 20 }}>×</Text>
            </TouchableOpacity>
          </View>

          {/* Scrollable diff — flex: 1 fills all space between header and buttons */}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ padding: 16 }}
            showsVerticalScrollIndicator={false}
          >
            {fields.map(({ key, label }) => {
              const differs = String(local[key] ?? "") !== String(server[key] ?? "");
              return (
                <View key={key} style={{ marginBottom: 24 }}>
                  <Text style={{ color: "#666", fontSize: 11, fontWeight: "600", marginBottom: 8, letterSpacing: 0.5 }}>
                    {label.toUpperCase()}
                  </Text>
                  <View style={{ flexDirection: "row", gap: 8 }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: "#888", fontSize: 10, fontWeight: "600", marginBottom: 4 }}>
                        YOUR VERSION
                      </Text>
                      <View style={{
                        backgroundColor: differs ? "#1a2e1a" : "#1e1e1e",
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: differs ? "#2d5a2d" : "#2a2a2a",
                        padding: 10,
                        minHeight: 44,
                      }}>
                        <Text style={{ color: differs ? "#7ec87e" : "#aaa", fontSize: 13, lineHeight: 18 }}>
                          {local[key] || <Text style={{ color: "#444", fontStyle: "italic" }}>empty</Text>}
                        </Text>
                      </View>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: "#888", fontSize: 10, fontWeight: "600", marginBottom: 4 }}>
                        SERVER VERSION
                      </Text>
                      <View style={{
                        backgroundColor: differs ? "#2e1a1a" : "#1e1e1e",
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: differs ? "#5a2d2d" : "#2a2a2a",
                        padding: 10,
                        minHeight: 44,
                      }}>
                        <Text style={{ color: differs ? "#c87e7e" : "#aaa", fontSize: 13, lineHeight: 18 }}>
                          {server[key] || <Text style={{ color: "#444", fontStyle: "italic" }}>empty</Text>}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              );
            })}
          </ScrollView>

          {/* Fixed buttons at bottom */}
          <View style={{
            flexDirection: "row",
            gap: 12,
            paddingHorizontal: 16,
            paddingTop: 12,
            paddingBottom: ENV.WEB ? 24 : 40,
            borderTopWidth: 1,
            borderTopColor: "#2a2a2a",
          }}>
            <TouchableOpacity
              onPress={() => this.resolveConflict(selectedConflict, false)}
              style={{
                flex: 1,
                backgroundColor: "#2d1a1a",
                borderWidth: 1,
                borderColor: "#5a2d2d",
                borderRadius: 8,
                paddingVertical: 12,
                alignItems: "center",
              }}
            >
              <Text style={{ color: "#c87e7e", fontSize: 13, fontWeight: "600" }}>Accept server</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => this.resolveConflict(selectedConflict, true)}
              style={{
                flex: 1,
                backgroundColor: "#1a2d1a",
                borderWidth: 1,
                borderColor: "#2d5a2d",
                borderRadius: 8,
                paddingVertical: 12,
                alignItems: "center",
              }}
            >
              <Text style={{ color: "#7ec87e", fontSize: 13, fontWeight: "600" }}>Keep mine</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  }

  render() {
    const { isOnline, offlineQueue, pendingConflicts, showDetail, selectedConflict } = this.state;
    const hasPending = offlineQueue.length > 0;
    const hasConflicts = pendingConflicts.length > 0;
    if (isOnline && !hasPending && !hasConflicts) return null;

    const count = offlineQueue.length;
    const bannerLabel = [
      !isOnline ? "● Offline" : null,
      hasPending ? `${count} pending update${count !== 1 ? "s" : ""}` : null,
      hasConflicts ? `${pendingConflicts.length} conflict${pendingConflicts.length !== 1 ? "s" : ""}` : null,
    ]
      .filter(Boolean)
      .join("  ·  ");

    return (
      <>
        {this.renderConflictDetail()}

        <TouchableOpacity
          onPress={() => this.setState({ showDetail: true })}
          activeOpacity={0.85}
          style={{
            backgroundColor: hasConflicts ? "#2a1500" : isOnline ? "#444" : "#1c1c1c",
            paddingTop: (ENV.WEB ? 0 : Constants.statusBarHeight) + 7,
            paddingBottom: 7,
            paddingHorizontal: 16,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: hasConflicts ? "#e67e22" : "#fff", fontSize: 12, opacity: 0.9 }}>
            {bannerLabel}
          </Text>
        </TouchableOpacity>

        <Modal
          visible={showDetail}
          transparent
          animationType="slide"
          onRequestClose={() => this.setState({ showDetail: false })}
        >
          <TouchableOpacity
            style={{ flex: 1, justifyContent: "flex-end" }}
            activeOpacity={1}
            onPress={() => this.setState({ showDetail: false })}
          >
            <TouchableOpacity activeOpacity={1}>
              <View
                style={{
                  backgroundColor: "#181818",
                  maxHeight: 500,
                  borderTopLeftRadius: 14,
                  borderTopRightRadius: 14,
                  paddingTop: 16,
                  paddingHorizontal: 16,
                  paddingBottom: ENV.WEB ? 24 : 32,
                }}
              >
                {/* Header */}
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: isOnline ? "#4caf50" : "#f44336",
                      marginRight: 8,
                    }}
                  />
                  <Text
                    style={{ color: "#fff", fontSize: 14, fontWeight: "600" }}
                  >
                    {isOnline ? "Connected" : "Offline"}
                  </Text>
                  {hasPending && (
                    <Text style={{ color: "#888", fontSize: 13, marginLeft: 8 }}>
                      — {count} pending update{count !== 1 ? "s" : ""}
                    </Text>
                  )}
                  {hasConflicts && (
                    <Text style={{ color: "#e67e22", fontSize: 13, marginLeft: 8 }}>
                      — {pendingConflicts.length} conflict{pendingConflicts.length !== 1 ? "s" : ""}
                    </Text>
                  )}
                </View>

                {/* Queue list */}
                {!hasPending ? (
                  <Text style={{ color: "#666", fontSize: 13, paddingVertical: 8 }}>
                    No pending updates. Changes will sync normally.
                  </Text>
                ) : (
                  <ScrollView
                    style={{ maxHeight: 220 }}
                    showsVerticalScrollIndicator={false}
                  >
                    {offlineQueue.map((item) => (
                      <View
                        key={item.id}
                        style={{
                          paddingVertical: 10,
                          borderBottomWidth: 1,
                          borderBottomColor: "#2a2a2a",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <View
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#666",
                            marginRight: 10,
                            flexShrink: 0,
                          }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={{ color: "#ddd", fontSize: 13 }}>
                            {item.description}
                          </Text>
                          <Text style={{ color: "#555", fontSize: 11, marginTop: 2 }}>
                            {new Date(item.timestamp).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </ScrollView>
                )}

                {/* Conflicts list */}
                {hasConflicts && (
                  <View style={{ marginTop: hasPending ? 16 : 0 }}>
                    <Text style={{ color: "#e67e22", fontSize: 11, fontWeight: "600", marginBottom: 8, letterSpacing: 0.5 }}>
                      SYNC CONFLICTS — tap to review
                    </Text>
                    {pendingConflicts.map((conflict) => (
                      <TouchableOpacity
                        key={conflict.id}
                        onPress={() => this.setState({ selectedConflict: conflict })}
                        style={{
                          paddingVertical: 10,
                          borderBottomWidth: 1,
                          borderBottomColor: "#2a2a2a",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Text style={{ color: "#ddd", fontSize: 13, flex: 1 }}>
                          {conflict.description}
                        </Text>
                        <Text style={{ color: "#555", fontSize: 12, marginLeft: 8 }}>›</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}

                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                  {hasPending && (
                    <TouchableOpacity
                      onPress={() => {
                        offlineQueueUtil.clearQueue();
                        this.setState({ showDetail: false });
                      }}
                    >
                      <Text style={{ color: "#c0392b", fontSize: 13 }}>Cancel pending</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    onPress={() => this.setState({ showDetail: false })}
                    style={hasPending ? {} : { flex: 1, alignItems: "center" }}
                  >
                    <Text style={{ color: "#555", fontSize: 13 }}>Dismiss</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      </>
    );
  }
}
